
import { computed, defineComponent, onMounted } from "vue";

import { NavigationGrid, NavigationGridItem } from "@tager/admin-ui";
import { useResource } from "@tager/admin-services";
import { getPageCount } from "@tager/admin-pages";
import { getFilesCount } from "@tager/admin-files";

import { LINKS } from "@/constants/links";
import {
  getBrandCount,
  getCategoryCount,
  getCollectionsCount,
  getProductCount,
  getProductTypeCount,
  getSpecialsCount,
  getTermCount,
} from "@/services/requests";

export default defineComponent({
  name: "HomePage",
  components: {
    NavigationGrid,
  },
  setup() {
    const [
      fetchProductCount,
      { data: productCountData, status: productCountDataStatus },
    ] = useResource({
      fetchResource: getProductCount,
      initialValue: null,
      resourceName: "Product count",
    });

    const [
      fetchCategoryCount,
      { data: categoryCountData, status: categoryCountDataStatus },
    ] = useResource({
      fetchResource: getCategoryCount,
      initialValue: null,
      resourceName: "Category count",
    });

    const [
      fetchFileCount,
      { data: fileCountData, status: fileCountDataStatus },
    ] = useResource({
      fetchResource: getFilesCount,
      initialValue: null,
      resourceName: "File count",
    });

    const [
      fetchSpecialsCount,
      { data: specialsCountData, status: specialsCountDataStatus },
    ] = useResource({
      fetchResource: getSpecialsCount,
      initialValue: null,
      resourceName: "Specials count",
    });

    const [
      fetchCollectionsCount,
      { data: collectionsCountData, status: collectionsCountDataStatus },
    ] = useResource({
      fetchResource: getCollectionsCount,
      initialValue: null,
      resourceName: "Collections count",
    });

    const [
      fetchBrandCount,
      { data: brandCountData, status: brandCountDataStatus },
    ] = useResource({
      fetchResource: getBrandCount,
      initialValue: null,
      resourceName: "Brand count",
    });

    const [
      fetchProductTypeCount,
      { data: productTypeCountData, status: productTypeCountDataStatus },
    ] = useResource({
      fetchResource: getProductTypeCount,
      initialValue: null,
      resourceName: "Product type count",
    });

    const [
      fetchTermCount,
      { data: termCountData, status: termCountDataStatus },
    ] = useResource({
      fetchResource: getTermCount,
      initialValue: null,
      resourceName: "Term count",
    });

    const [
      fetchPageCount,
      { data: pageCountData, status: pageCountDataStatus },
    ] = useResource({
      fetchResource: () => getPageCount(),
      initialValue: null,
      resourceName: "Page count",
    });

    onMounted(() => {
      fetchProductCount();
      fetchCategoryCount();
      fetchBrandCount();
      fetchSpecialsCount();
      fetchCollectionsCount();
      fetchProductTypeCount();
      fetchTermCount();
      fetchPageCount();
      fetchFileCount();
    });

    const navItemList = computed<Array<NavigationGridItem>>(() => [
      {
        name: LINKS.PRODUCT_LIST.text,
        total: {
          value: productCountData.value?.count ?? 0,
          status: productCountDataStatus.value,
        },
        url: LINKS.PRODUCT_LIST.url,
      },
      {
        name: LINKS.CATEGORY_LIST.text,
        total: {
          value: categoryCountData.value?.count ?? 0,
          status: categoryCountDataStatus.value,
        },
        url: LINKS.CATEGORY_LIST.url,
      },

      {
        name: LINKS.SPECIAL_LIST.text,
        total: {
          value: specialsCountData.value?.count ?? 0,
          status: specialsCountDataStatus.value,
        },
        url: LINKS.SPECIAL_LIST.url,
      },
      {
        name: LINKS.COLLECTION_LIST.text,
        total: {
          value: collectionsCountData.value?.count ?? 0,
          status: collectionsCountDataStatus.value,
        },
        url: LINKS.COLLECTION_LIST.url,
      },
      {
        name: LINKS.BRANDS_LIST.text,
        total: {
          value: brandCountData.value?.count ?? 0,
          status: brandCountDataStatus.value,
        },
        url: LINKS.BRANDS_LIST.url,
      },
      {
        name: LINKS.PRODUCT_TYPE_LIST.text,
        total: {
          value: productTypeCountData.value?.count ?? 0,
          status: productTypeCountDataStatus.value,
        },
        url: LINKS.PRODUCT_TYPE_LIST.url,
      },
      {
        name: LINKS.TERM.text,
        url: LINKS.TERM.url,
        total: {
          value: termCountData.value?.count ?? 0,
          status: termCountDataStatus.value,
        },
      },
      {
        name: LINKS.PAGE_LIST.text,
        url: LINKS.PAGE_LIST.url,
        total: {
          value: pageCountData.value?.count ?? 0,
          status: pageCountDataStatus.value,
        },
      },
      {
        name: LINKS.FILES_LIST.text,
        url: LINKS.FILES_LIST.url,
        total: {
          value: fileCountData.value?.count ?? 0,
          status: fileCountDataStatus.value,
        },
      },
      {
        name: "SEO",
        linkList: [LINKS.SEO_LIST_CATEGORY, LINKS.SEO_TEMPLATES],
      },
      {
        name: "Блог",
        linkList: [LINKS.BLOG_POST_LIST, LINKS.BLOG_CATEGORY_LIST],
      },
      {
        name: "Меню",
        linkList: [
          LINKS.MENU_TOP,
          LINKS.MENU_MAIN,
          LINKS.MENU_CATALOG,
          LINKS.MENU_BOTTOM,
        ],
      },
      {
        name: "E-Mail",
        linkList: [LINKS.EMAIL_TEMPLATE_LIST, LINKS.EMAIL_LOG_LIST],
      },
      {
        name: "Экспорт / Импорт",
        linkList: [LINKS.EXPORT_LIST, LINKS.IMPORT_LIST],
      },
      {
        name: "Настройки",
        url: LINKS.SETTINGS_COMMON.url,
      },
    ]);

    return {
      navItemList,
    };
  },
});
